import React, { useEffect, useId, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import Input from 'src/components/form/floating-label/input';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import API from '../data/events-api';
import DateTimePicker from 'src/components/form/floating-label/date-time-picker';
import Modal from 'src/components/common/modal/modal';
import { env } from 'src/constants/env';
import { fireEvent } from 'src/utils/analytics/google';

// styles
import css from './propose-event.module.scss';
import 'src/utils/popup.scss';
import getValidationSchema from './validate';
import { toast } from 'react-toastify';
import { ONLINE } from 'src/constants/locations';
import { CreateEventPropositionRequest, CreateEventPropositionData } from '../types';

interface ProposeEventPopupProps {
  trigger: React.ReactNode;
}

export default function ProposeEventPopup({ trigger }: Readonly<ProposeEventPopupProps>) {
  const validationSchema = getValidationSchema();
  const captchaRef = useRef<ReCAPTCHA>(null);
  const registerAndClearOnChange = function (inputName: keyof CreateEventPropositionData) {
    return register(inputName, {
      onChange: () => {
        if (errors[inputName]) {
          clearErrors(inputName);
        }
      }
    });
  };

  const methods = useForm<CreateEventPropositionData>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      organizer: '',
      url: '',
      scheduledAt: null,
      locationId: '',
      phone: '',
      email: '',
      description: ''
    }
  });
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    getValues,
    control,
    trigger: triggerValidation,
    formState: { errors, isSubmitSuccessful, defaultValues, isDirty, isValid }
  } = methods;
  const values = getValues();

  const isEdited = (Object.keys(values) as (keyof CreateEventPropositionData)[]).some(
    (key) => values[key] !== defaultValues?.[key]
  );

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
      captchaRef.current?.reset();
    }
  }, [isSubmitSuccessful, reset]);

  const onFormSubmit = async (data: CreateEventPropositionRequest, close: () => void) => {
    try {
      const response = await API.proposeEvent({
        ...data,
        scheduledAt: data.scheduledAt ? new Date(data.scheduledAt).getTime() : null
      });
      if (response.status === 201) {
        toast.success(`Пропозиція для створення події надіслана! Модератор опрацює Ваш запит`);
        reset();
        close();
        fireEvent('propose_event_form_submit', data);
      }
    } catch (error) {
      toast.error('Виникла помилка. Спробуйте, будь ласка, пізніше.');
    }
  };

  const titleInputId = useId();
  const organizerInputId = useId();
  const urlInputId = useId();
  const locationInputId = useId();
  const phoneInputId = useId();
  const emailInputId = useId();
  const descriptionInputId = useId();

  return (
    <Modal trigger={trigger} onClose={reset} nested closeOnDocumentClick={!isEdited}>
      {(close: () => void) => (
        <div className={css.proposeEvent}>
          <button className={css.close} onClick={close}>
            &times;
          </button>
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(async (data) => {
                const token = await captchaRef.current?.executeAsync();
                onFormSubmit({ ...data, captcha: token }, close);
              })}>
              <legend>
                <h1>Заповніть форму для створення події</h1>
              </legend>
              <fieldset>
                <Input
                  label='Назва події'
                  id={titleInputId}
                  error={errors?.title?.message}
                  required
                  {...registerAndClearOnChange('title')}
                />
                <Input
                  label='Організатор'
                  id={organizerInputId}
                  error={errors?.organizer?.message}
                  required
                  {...registerAndClearOnChange('organizer')}
                />
                <Input
                  label='Посилання на подію'
                  id={urlInputId}
                  error={errors?.url?.message}
                  required
                  {...registerAndClearOnChange('url')}
                />
                <div className={css.group}>
                  <DateTimePicker control={control} triggerValidation={triggerValidation} required name='scheduledAt' />
                  <Input
                    label='Локація'
                    id={locationInputId}
                    name='locationId'
                    triggerValidation={triggerValidation}
                    clearErrors={clearErrors}
                    error={errors?.locationId?.message}
                    defaultLocation={ONLINE}
                    required
                    searchSelect
                  />
                </div>
                <div className={css.group}>
                  <Input
                    label='Контактний тел.'
                    id={phoneInputId}
                    placeholder='+380... або 0800...'
                    type='phone'
                    error={errors?.phone?.message}
                    required
                    {...registerAndClearOnChange('phone')}
                  />
                  <Input
                    label='Ел. пошта'
                    id={emailInputId}
                    type='email'
                    error={errors?.email?.message}
                    required
                    {...registerAndClearOnChange('email')}
                  />
                </div>
                <Input
                  label='Коментар'
                  id={descriptionInputId}
                  multiline
                  error={errors?.description?.message}
                  {...registerAndClearOnChange('description')}
                />
              </fieldset>
              <button disabled={!isDirty || !isValid} type='submit'>
                Надіслати
              </button>
            </form>
            <ReCAPTCHA
              ref={captchaRef}
              className={css.captcha}
              sitekey={env.REACT_APP_NZ_RECAPTCHA_SITE_KEY}
              hl='uk'
              size='invisible'
            />
          </FormProvider>
        </div>
      )}
    </Modal>
  );
}
